import React, { createContext, useState, useEffect, useCallback } from "react";
import axiosJwt from "../services/axios";
import Amplify from "aws-amplify";
import {
  QuestionFeedback,
  TargetsEntity,
  QuestionsEntity,
  CourseStructure,
  ModulesEntity,
  ModuleEntity,
  IBackBehavior,
  AllCourses,
  SkillsEntity,
  ModuleTargetFilter,
} from "../interfaces";
import { cognitoConfig } from "../CognitoConfig";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { logger } from "../services/logger";
import { CourseTree } from "../components/utils/CourseTree";
import { QuestionType, _AuthType } from "../enums";
import { IUseMacsAuthenticator } from "../hooks/useMacsAuthenticator";
import axios from "axios";
import { CourseMetadata } from "@backend-types";

Amplify.configure(cognitoConfig);

interface CourseStructureProviderProps {
  authHook: IUseMacsAuthenticator;
  children: React.ReactNode;
}

export const CourseStructureContext = createContext({});

export const CourseStructureProvider: React.FC<CourseStructureProviderProps> = ({
  authHook,
  children,
}) => {
  const authStatus = authHook.authenticated;

  const [modulesEndpointIsLoading, setModulesEndpointIsLoading] = useState(false);
  const [questionFeedback, setQuestionFeedback] = useState<
    QuestionFeedback | undefined
  >();
  // determines if the learning experience back button takes the user back to home page or module overview
  // received confirmation to make default back button behavior go to module overview
  const [deeplinkMode, setDeeplinkMode] = useState<IBackBehavior | null>(null);
  const [backBehavior, setBackBehavior] = useState<IBackBehavior>({
    backTo: "moduleoverview",
  });

  const [courseTreeStructure, setCourseTreeStructure] = useState<CourseTree>();
  const [courseTreeProgressInitialized, setCourseTreeProgressInitialized] =
    useState<boolean>(false);

  const history = useHistory();

  // useLocation rerenders the component when the location changes
  const location = useLocation();
  const currentModuleId = new URLSearchParams(location.search).get("moduleId");
  const currentQuestionId = new URLSearchParams(location.search).get("questionId");
  const _currentCourseId = new URLSearchParams(location.search).get("courseId");
  const [currentCourseId, setCurrentCourseId] = useState<number | null>(null);
  const [courseLoadedSuccessfully, setCourseLoadedSuccessfully] = useState(false);
  const [allCourses, setAllCourses] = useState<AllCourses[]>([]);

  // const [currentLaunchContextQuestionId, setCurrentLaunchContextQuestionId] =
  //   useState<any>(null);
  // const [
  //   currentLaunchContextQuestionSetSuccess,
  //   setCurrentLaunchContextQuestionSetSuccess
  // ] = useState<boolean>(false);

  // this will force the back behavior to return to deep linking page
  // in the event that user previews and tries to return
  useEffect(() => {
    if (deeplinkMode && backBehavior && backBehavior.backTo !== "deeplinking") {
      if (backBehavior.backTo === "home" || location.pathname === "/modules") {
        setBackBehavior({ ...deeplinkMode });
      }
    }
  }, [deeplinkMode, backBehavior, location.pathname]);

  // if no target or question Id, forward to first target and first question
  // if the user goes to /modules with no module ID specified, go to first module
  // if user goes to the learning experience with no module ID, target ID, or question ID
  // forward to first target or question, respectively.
  // used to forward users to next target or module from several places without traversing the maps or arrays in course structure.
  // also prevents users from going to locked content.
  useEffect(() => {
    if (!courseTreeStructure) return;
    const updateSearchParamsPaths = ["/modules"];
    const learningExperiencePaths = ["/solver"];
    if (updateSearchParamsPaths.includes(location.pathname)) {
      if (!currentModuleId) {
        history.push({
          search: new URLSearchParams({
            moduleId: courseTreeStructure?.getFirstModuleInCourse()?.id!,
          }).toString(),
        });
      }
    }

    if (learningExperiencePaths.includes(location.pathname)) {
      if (!currentQuestionId) {
        history.push({
          search: new URLSearchParams({
            questionId: courseTreeStructure?.getFirstQuestionInCourse()?.id!,
          }).toString(),
        });
      }
    }
  }, [history, location, currentQuestionId, currentModuleId, courseTreeStructure]);

  useEffect(() => {
    if (!authStatus) return;
    if (_currentCourseId) {
      setCurrentCourseId(Number(_currentCourseId));
    }
  }, [_currentCourseId, authStatus]);

  // Used for insights filtering and querying
  const getModuleFiltersInCourse = useCallback(
    (courseId: number): null | ModuleTargetFilter[] => {
      if (!allCourses) return null;
      return (
        allCourses
          .find((course) => course.id === courseId)
          ?.modules.map((modulesInCourse) => {
            return {
              id: modulesInCourse.module.id,
              name: modulesInCourse.module.name,
            };
          }) ?? null
      );
    },
    [allCourses]
  );

  // Used for insights filtering and querying
  const getMetadataInCourse = useCallback(
    (courseId: number): undefined | CourseMetadata => {
      if (!allCourses) return undefined;
      return allCourses.find((course) => course.id === courseId)?.metadata;
    },
    [allCourses]
  );

  const getTargetFiltersInCourse = useCallback(
    (courseId: number, moduleId?: string): null | ModuleTargetFilter[] => {
      if (!allCourses) return null;
      const moduleTargets =
        allCourses
          .find((course) => course.id === courseId)
          ?.modules.map((modulesInCourse) => {
            if (modulesInCourse.module.id === moduleId) {
              return modulesInCourse.module.targets.map((target) => {
                return { name: target.name, id: target.id };
              });
            } else {
              return [];
            }
          }) ?? null;
      const targets = moduleTargets?.flat();
      return targets ?? null;
    },
    [allCourses]
  );
  //

  useEffect(() => {
    const source = axios.CancelToken.source();

    const getCourse = async () => {
      try {
        const queryParams = {
          ...(currentModuleId && { module_id: currentModuleId }),
          ...(currentQuestionId && { question_id: currentQuestionId }),
          ...(authHook.authType === _AuthType.LTI &&
            authHook.ltik && { ltik: authHook.ltik }),
          bypassCache: true,
        };
        const res = await axiosJwt.get(`/courses`, {
          cancelToken: source.token,
          params: queryParams,
        });
        return res.data;
      } catch (e) {
        logger.error(e);
        throw e;
      }
    };
    if (courseLoadedSuccessfully && allCourses.length > 0) return;
    if (!authStatus) return;
    if (!currentCourseId) {
      getCourse().then((course) => {
        setAllCourses(course);
        if (course && course.length > 0) {
          if (!currentCourseId) {
            setCurrentCourseId(course[0].id);
          }
        } else {
          throw new Error("Failed to retrieve course structure");
        }
      });
    } else if (currentCourseId && allCourses.length === 0) {
      getCourse().then((course) => {
        setAllCourses(course);
      });
    }

    return () => {
      source.cancel("Component unmounted or dependencies changed"); // Cancel the request on cleanup
    };
  }, [
    authHook.authType,
    authHook.ltik,
    currentCourseId,
    courseLoadedSuccessfully,
    currentQuestionId,
    currentModuleId,
    authStatus,
    allCourses.length,
  ]);

  useEffect(() => {
    if (!authStatus) return;
    if (!_currentCourseId) return;
    setCourseLoadedSuccessfully(false);
  }, [_currentCourseId, authStatus]);

  useEffect(() => {
    if (!authStatus) return;
    if (!currentCourseId) return;
    if (courseLoadedSuccessfully) return;

    const source = axios.CancelToken.source();
    // if (currentLaunchContextQuestionSetSuccess) {
    //   return;
    // }
    const getAllModules = async () => {
      try {
        let courseEndpoint = `/courses/${currentCourseId}`;
        // See comment below
        // if (MacsAuthenticator.authHook.authType === _AuthType.LTI && MacsAuthenticator.authHook.ltik) {
        //   courseEndpoint = `/courses/${currentCourseId}?ltik=${
        //     MacsAuthenticator.authHook.ltik ?? new URLSearchParams(location.search).get("ltik")
        //   }`;
        //   // setCurrentLaunchContextQuestionId(currentQuestionId);
        //   // setCurrentLaunchContextQuestionSetSuccess(true);
        // }
        setModulesEndpointIsLoading(true);
        const courseTree = new CourseTree();
        console.log(">>> courseEndpoint", courseEndpoint);
        const ltik = authHook.ltik ?? new URLSearchParams(location.search).get("ltik");
        const res = await axiosJwt.get(courseEndpoint, {
          cancelToken: source.token,
          params: {
            ...(ltik && { ltik: ltik }), // Always pass LTIK if its available, seems like authType wasn't always being set
          },
        });
        const courseDetails = res.data as CourseStructure;
        const courseId = courseDetails.id.toString();
        courseTree.addCourse(
          courseId,
          courseDetails.name,
          courseDetails.description,
          courseDetails.metadata,
          courseDetails.higher_ed
        );
        const ModuleIdsAndNames = courseDetails.modules;
        ModuleIdsAndNames.forEach((module: ModulesEntity) => {
          let _module = module.module as ModuleEntity;
          courseTree.addModule(_module, courseId);
          _module.targets.forEach((target: TargetsEntity) => {
            courseTree.addTarget(target, _module.id);
            let practiceProblemNumber = 1001;
            let masteryProblemNumber = 2001;
            let guidedPracticeProblemNumber = 1;
            target.questions.forEach((question: QuestionsEntity) => {
              if (question.type === QuestionType.guided) {
                courseTree.addQuestion(
                  question,
                  target.id,
                  _module.id,
                  guidedPracticeProblemNumber
                );
                guidedPracticeProblemNumber++;
              } else if (question.type === QuestionType.practice) {
                courseTree.addQuestion(
                  question,
                  target.id,
                  _module.id,
                  practiceProblemNumber
                );
                practiceProblemNumber++;
              } else if (question.type === QuestionType.mastery) {
                courseTree.addQuestion(
                  question,
                  target.id,
                  _module.id,
                  masteryProblemNumber
                );
                masteryProblemNumber++;
              }
              question.question_skills?.forEach((skill) => {
                courseTree.addQuestionSkill(skill.skill, question.id);
              });
            });
            target.target_skills?.forEach((skill: SkillsEntity) => {
              courseTree.addTargetSkill(skill.skill, target.id, skill.order);
            });
          });
        });
        setCourseTreeStructure(courseTree);
        setCourseLoadedSuccessfully(true);
        setModulesEndpointIsLoading(false);
      } catch (err) {
        logger.error(err);
      }
    };
    getAllModules();

    return () => {
      source.cancel("Component unmounted or dependencies changed"); // Cancel the request on cleanup
    };
  }, [
    authHook.ltik,
    authStatus,
    courseLoadedSuccessfully,
    currentCourseId,
    currentQuestionId,
    location.search,
    // currentLaunchContextQuestionId,
    // currentLaunchContextQuestionSetSuccess
  ]);

  const updateCourseMetadata = useCallback(
    (courseId: number, newMetadata: CourseMetadata) => {
      // 1) Update allCourses (immutable update so React re-renders)
      setAllCourses((prev) =>
        prev.map((course) =>
          course.id === courseId
            ? {
                ...course,
                metadata: newMetadata,
              }
            : course
        )
      );

      // 2) If this is the current course, update the in-memory course tree too
      if (courseId === currentCourseId) {
        courseTreeStructure?.setCurrentCourseMetadata(newMetadata);
      }
    },
    [currentCourseId, courseTreeStructure]
  );

  return (
    <CourseStructureContext.Provider
      value={{
        currentModuleId,
        currentQuestionId,
        modulesEndpointIsLoading,
        questionFeedback,
        setQuestionFeedback,
        deeplinkMode,
        setDeeplinkMode,
        backBehavior,
        setBackBehavior,
        courseTreeStructure,
        allCourses,
        setCourseLoadedSuccessfully,
        courseLoadedSuccessfully,
        courseTreeProgressInitialized,
        setCourseTreeProgressInitialized,
        getModuleFiltersInCourse,
        getMetadataInCourse,
        updateCourseMetadata,
        getTargetFiltersInCourse,
      }}>
      {children}
    </CourseStructureContext.Provider>
  );
};
